import { cls } from "../utils";
import { useMemo, type HTMLAttributes } from "react";
import "./MediaPreview.css";

export interface MediaPreviewProps
  extends HTMLAttributes<HTMLImageElement | HTMLVideoElement> {
  src: string;
  mimeType: string;
  width?: number;
  height?: number;
  alt?: string;
  cover?: boolean;
  loading?: "lazy" | "eager";
  autoPlay?: boolean;
}

interface ImagePreviewProps extends HTMLAttributes<HTMLImageElement> {
  src: string;
  alt?: string;
  loading?: "lazy" | "eager";
}

const ImagePreview = ({
  src,
  className,
  alt,
  loading = "lazy",
  ...props
}: ImagePreviewProps) => {
  return (
    <img
      src={src}
      className={className}
      alt={alt}
      loading={loading}
      {...props}
    />
  );
};

interface VideoPreviewProps extends HTMLAttributes<HTMLVideoElement> {
  src: string;
  alt?: string;
  loading?: "lazy" | "eager";
  autoPlay?: boolean;
}

const VideoPreview = ({
  src,
  className,
  alt,
  loading = "lazy",
  autoPlay,
  ...props
}: VideoPreviewProps) => {
  return (
    <video
      title={alt}
      src={src}
      className={className}
      autoPlay={autoPlay}
      loop
      muted
      playsInline
      preload={loading === "eager" ? "auto" : "metadata"}
      {...props}
    />
  );
};

export const MediaPreview = ({
  src,
  mimeType,
  width,
  height,
  alt,
  cover = false,
  loading = "lazy",
  autoPlay = true,
  ...props
}: MediaPreviewProps) => {
  const ReturnComp = useMemo(() => {
    if (mimeType.startsWith("image/")) {
      return ImagePreview;
    }
    if (mimeType.startsWith("video/")) {
      return VideoPreview;
    }
    return ImagePreview;
  }, [mimeType]);

  if (!ReturnComp) return null;

  const aspectRatio = useMemo(() => {
    if (!width || !height) return "auto";
    return width / height;
  }, [width, height]);

  return (
    <div
      className={cls("media-preview", cover ? "cover" : "fit", props.className)}
      // @ts-ignore
      style={{ ["--aspect-ratio"]: aspectRatio }}
    >
      <ReturnComp
        src={src}
        alt={alt}
        loading={loading}
        autoPlay={autoPlay}
        {...props}
      />
    </div>
  );
};
